// client/src/components/ContractLineItemsSection.jsx
// Collapsible section on the job Payments tab for managing contract line items
// and viewing the AR summary bar.
import { useState, useEffect, useCallback } from 'react';
import { showToast } from '../utils/toast';
import { showConfirm } from '../utils/confirm';

const BLUE = '#1B3A6B';
const GREEN = '#2E7D32';
const ORANGE = '#E07B2A';
const RED = '#C62828';
const TEAL = '#0D9488';

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function pct(part, whole) {
  if (!whole || whole <= 0) return 0;
  return Math.min(100, (part / whole) * 100);
}

export default function ContractLineItemsSection({ jobId, token, job, onUpdated }) {
  const [collapsed, setCollapsed] = useState(false);
  const [arSummary, setArSummary] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const headers = { 'x-auth-token': token, 'Content-Type': 'application/json' };

  const loadArSummary = useCallback(() => {
    fetch(`/api/jobs/${jobId}/ar-summary`, { headers: { 'x-auth-token': token } })
      .then((r) => r.json())
      .then((d) => setArSummary(d))
      .catch(() => {});
  }, [jobId, token]);

  useEffect(() => {
    loadArSummary();
  }, [loadArSummary]);

  // Parse contract line items from job
  const contractItems = arSummary?.contract_line_items || (() => {
    try { return JSON.parse(job?.contract_line_items || '[]'); } catch { return []; }
  })();

  const startEditing = () => {
    setDraftItems(
      contractItems.length
        ? contractItems.map((ci) => ({ ...ci }))
        : [{ id: '', description: '', amount: '' }],
    );
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraftItems([]);
  };

  const addRow = () =>
    setDraftItems((prev) => [...prev, { id: '', description: '', amount: '' }]);

  const removeRow = async (idx) => {
    const item = draftItems[idx];
    if ((parseFloat(item.invoiced_amount) || 0) > 0) {
      const ok = await showConfirm(
        `This item has ${fmt(item.invoiced_amount)} already invoiced. Remove it anyway?`,
      );
      if (!ok) return;
    }
    setDraftItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) =>
    setDraftItems((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );

  const saveItems = async () => {
    for (let i = 0; i < draftItems.length; i++) {
      if (!draftItems[i].description.trim()) {
        showToast(`Row ${i + 1}: description is required`, 'error');
        return;
      }
      if (draftItems[i].amount === '' || isNaN(Number(draftItems[i].amount))) {
        showToast(`Row ${i + 1}: enter a valid amount`, 'error');
        return;
      }
    }
    setSaving(true);
    const res = await fetch(`/api/jobs/${jobId}/contract-line-items`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({
        items: draftItems.map((r) => ({
          id: r.id || undefined,
          description: r.description.trim(),
          amount: parseFloat(r.amount) || 0,
        })),
      }),
    });
    const data = await res.json();
    setSaving(false);
    if (res.ok) {
      setEditing(false);
      loadArSummary();
      onUpdated?.();
      showToast('Contract line items saved');
    } else {
      showToast(data.error || 'Failed to save', 'error');
    }
  };

  const summary = arSummary || { contract_total: 0, invoiced: 0, collected: 0, remaining: 0 };
  const { contract_total, invoiced, collected, remaining } = summary;
  const invoicedPct = pct(invoiced, contract_total);
  const collectedPct = pct(collected, contract_total);

  const hasItems = contractItems.length > 0;

  return (
    <div
      style={{
        background: '#f0f8ff',
        border: '1.5px solid #c7ddf5',
        borderRadius: 10,
        marginBottom: 20,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          cursor: 'pointer',
          background: '#e8f0fb',
          borderBottom: collapsed ? 'none' : '1px solid #c7ddf5',
        }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, color: BLUE, fontSize: 13 }}>
            📋 Contract Line Items
          </span>
          {hasItems && (
            <span
              style={{
                background: BLUE,
                color: 'white',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 7px',
              }}
            >
              {contractItems.length} item{contractItems.length !== 1 ? 's' : ''}
            </span>
          )}
          {contract_total > 0 && (
            <span style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>
              {fmt(contract_total)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!collapsed && !editing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                startEditing();
              }}
              style={{
                padding: '4px 12px',
                background: BLUE,
                color: 'white',
                border: 'none',
                borderRadius: 5,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {hasItems ? 'Edit' : '+ Add Items'}
            </button>
          )}
          <span style={{ color: '#888', fontSize: 14 }}>{collapsed ? '▶' : '▼'}</span>
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <div style={{ padding: '14px 16px' }}>
          {/* Editing mode */}
          {editing ? (
            <div>
              <div style={{ overflowX: 'auto', marginBottom: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: BLUE, color: 'white' }}>
                      <th style={{ padding: '7px 8px', textAlign: 'left', fontWeight: 600, fontSize: 11 }}>#</th>
                      <th style={{ padding: '7px 8px', textAlign: 'left', fontWeight: 600, fontSize: 11 }}>Description</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600, fontSize: 11 }}>Amount</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600, fontSize: 11 }}>Already Invoiced</th>
                      <th style={{ padding: '7px 4px', textAlign: 'center', fontWeight: 600, fontSize: 11 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftItems.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '6px 8px', color: '#888', fontSize: 11 }}>{idx + 1}</td>
                        <td style={{ padding: '4px 6px' }}>
                          <input
                            value={item.description}
                            onChange={(e) => updateRow(idx, 'description', e.target.value)}
                            placeholder="e.g. Roofing — Full Replacement"
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1.5px solid #C8D4E4',
                              borderRadius: 5,
                              fontSize: 12,
                              boxSizing: 'border-box',
                            }}
                          />
                        </td>
                        <td style={{ padding: '4px 6px', minWidth: 110 }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.amount}
                            onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                            placeholder="0.00"
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1.5px solid #C8D4E4',
                              borderRadius: 5,
                              fontSize: 12,
                              textAlign: 'right',
                              boxSizing: 'border-box',
                            }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#555', fontSize: 12 }}>
                          {item.invoiced_amount > 0 ? fmt(item.invoiced_amount) : '—'}
                        </td>
                        <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                          {draftItems.length > 1 && (
                            <button
                              onClick={() => removeRow(idx)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: RED,
                                fontSize: 16,
                                lineHeight: 1,
                                padding: '2px 4px',
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals row */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12, marginBottom: 10, color: BLUE, fontWeight: 700 }}>
                Total:{' '}
                {fmt(draftItems.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))}
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={addRow}
                  style={{
                    background: 'none',
                    border: `1px dashed ${BLUE}`,
                    color: BLUE,
                    borderRadius: 5,
                    padding: '4px 12px',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  + Add Row
                </button>
                <div style={{ flex: 1 }} />
                <button
                  onClick={cancelEditing}
                  style={{
                    padding: '6px 12px',
                    background: 'none',
                    border: '1px solid #ccc',
                    borderRadius: 5,
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#666',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveItems}
                  disabled={saving}
                  style={{
                    padding: '6px 16px',
                    background: BLUE,
                    color: 'white',
                    border: 'none',
                    borderRadius: 5,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save Items'}
                </button>
              </div>
            </div>
          ) : hasItems ? (
            /* View mode — item table */
            <div>
              <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#dce8f7' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: BLUE, fontWeight: 700, fontSize: 11 }}>#</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: BLUE, fontWeight: 700, fontSize: 11 }}>Description</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', color: BLUE, fontWeight: 700, fontSize: 11 }}>Contract $</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', color: BLUE, fontWeight: 700, fontSize: 11 }}>Invoiced</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', color: BLUE, fontWeight: 700, fontSize: 11 }}>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractItems.map((ci, idx) => {
                      const inv = parseFloat(ci.invoiced_amount) || 0;
                      const rem = Math.max(0, (ci.amount || 0) - inv);
                      const fullyBilled = rem <= 0.005;
                      return (
                        <tr
                          key={ci.id || idx}
                          style={{
                            borderBottom: '1px solid #e2e8f0',
                            background: fullyBilled ? '#f0fdf4' : 'white',
                          }}
                        >
                          <td style={{ padding: '7px 8px', color: '#888', fontSize: 11 }}>{idx + 1}</td>
                          <td style={{ padding: '7px 8px', color: '#222' }}>
                            {ci.description}
                            {fullyBilled && (
                              <span
                                style={{
                                  marginLeft: 7,
                                  fontSize: 9,
                                  background: '#dcfce7',
                                  color: '#16a34a',
                                  border: '1px solid #86efac',
                                  borderRadius: 8,
                                  padding: '1px 6px',
                                  fontWeight: 700,
                                }}
                              >
                                Fully Billed ✓
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600, color: BLUE }}>{fmt(ci.amount)}</td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', color: inv > 0 ? TEAL : '#aaa' }}>
                            {inv > 0 ? fmt(inv) : '—'}
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700, color: fullyBilled ? GREEN : ORANGE }}>
                            {fullyBilled ? fmt(0) : fmt(rem)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#dce8f7', fontWeight: 700 }}>
                      <td colSpan={2} style={{ padding: '7px 8px', color: BLUE, fontSize: 12 }}>Total</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: BLUE }}>{fmt(contract_total)}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: TEAL }}>{fmt(invoiced)}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: remaining > 0 ? ORANGE : GREEN }}>
                        {fmt(remaining)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* AR Summary Bar */}
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  AR Status
                </div>
                <div style={{ display: 'flex', gap: 0, borderRadius: 7, overflow: 'hidden', border: '1px solid #c7ddf5', marginBottom: 8 }}>
                  {[
                    { label: 'Contract', value: contract_total, color: BLUE },
                    { label: 'Invoiced', value: invoiced, color: TEAL },
                    { label: 'Collected', value: collected, color: GREEN },
                    { label: 'Remaining', value: remaining, color: remaining > 0 ? ORANGE : GREEN },
                  ].map(({ label, value, color }, i) => (
                    <div
                      key={label}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        background: 'white',
                        borderRight: i < 3 ? '1px solid #c7ddf5' : 'none',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: 10, color: '#888', fontWeight: 600, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color }}>{fmt(value)}</div>
                    </div>
                  ))}
                </div>
                {/* Progress bar */}
                {contract_total > 0 && (
                  <div style={{ position: 'relative', height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${invoicedPct}%`,
                        background: TEAL,
                        borderRadius: 4,
                        transition: 'width 0.4s',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${collectedPct}%`,
                        background: GREEN,
                        borderRadius: 4,
                        transition: 'width 0.4s',
                      }}
                    />
                  </div>
                )}
                {contract_total > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginTop: 3 }}>
                    <span style={{ color: TEAL }}>▪ Invoiced {Math.round(invoicedPct)}%</span>
                    <span style={{ color: GREEN }}>▪ Collected {Math.round(collectedPct)}%</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Empty state */
            <div style={{ textAlign: 'center', padding: '18px 0', color: '#888', fontSize: 12 }}>
              <div style={{ marginBottom: 8 }}>No contract line items added yet.</div>
              <div style={{ color: '#aaa', fontSize: 11, marginBottom: 12 }}>
                Adding line items lets you track exactly what's been invoiced for each scope of work.
              </div>
              <button
                onClick={startEditing}
                style={{
                  padding: '7px 18px',
                  background: BLUE,
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                + Add Contract Line Items
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
